import React, { useMemo } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { SelectOption } from '../../interfaces';
import { ComboboxOption } from './ComboboxOption';

const optionClassName =
  'relative cursor-default select-none px-3 py-2 ui-active:bg-foundation-primary ui-active:text-white ui-not-active:text-text-default-content';

export const ComboboxOptions: React.FC<{
  filteredOptions: SelectOption[];
  isMenuPlacementTop: boolean;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
  children?: React.ReactNode;
}> = ({ filteredOptions, isMenuPlacementTop, formatOptionLabel, children }) => {
  if (filteredOptions.length === 0 && !children) {
    return null;
  }
  return (
    <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
      <Combobox.Options
        className={classNames(
          {
            '-top-2 -translate-y-full transform': isMenuPlacementTop,
          },
          'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'
        )}
      >
        {filteredOptions.map((option) => (
          <Combobox.Option key={String(option.value)} className={optionClassName} value={option}>
            <ComboboxOption option={option} formatOptionLabel={formatOptionLabel} />
          </Combobox.Option>
        ))}
        {children}
      </Combobox.Options>
    </Transition>
  );
};

export const CreatableComboboxOptions: React.FC<{
  filteredOptions: SelectOption[];
  isMenuPlacementTop: boolean;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
  query: string;
}> = ({ query, filteredOptions, ...rest }) => {
  const setOfAllValues = useMemo(() => new Set(filteredOptions.map((option) => option.value)), [filteredOptions]);
  const candidateOption: SelectOption = { label: query, value: query };
  const showCreateOption = query.length > 0 && !setOfAllValues.has(query);
  return (
    <ComboboxOptions filteredOptions={filteredOptions} {...rest}>
      {showCreateOption && (
        <Combobox.Option key={String(candidateOption.value)} value={candidateOption} className={optionClassName}>
          {`Create ${candidateOption.label}`}
        </Combobox.Option>
      )}
    </ComboboxOptions>
  );
};
